import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, LoadingService, memo } from '@scandium-oy/ngx-scandium';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from '../common.module';
import { PickMediaComponent } from '../components2/pick-media/pick-media.component';
import { IAttachment } from '../models/attachment.model';
import { FileUpload } from '../models/file.model';
import { ILanguage } from '../models/language.model';
import { Message } from '../models/message.model';
import { User } from '../models/user.model';
import { ContractorsService } from '../services/contractors.service';
import { MessagesService } from '../services/messages.service';
import { NavigationService } from '../services/navigation.service';
import { PushNotificationsService } from '../services/push-notifications.service';
import { ACCENT_COLORS, ThemeService } from '../services/theme.service';
import { UploadService } from '../services/upload.service';
import { UsersService } from '../services/users.service';
import { WorkerMessagesService } from '../services/worker-messages.service';
import { ModalShellComponent } from '../ui/modal-shell/modal-shell.component';
import { Languages, notificationKeys, userDocuments } from '../utility/constants';
import { validateEmail } from '../utility/item';
import { sortAnyByDate } from '../utility/sort';
import { ticketCreated } from '../utility/ticket';

export const validateSame: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const first = control.parent?.get('newPassword').value;
  const second = control.parent?.get('newPasswordAgain').value;
  if (first !== second) {
    return { notSame: 'Passwords are not the same.' };
  }
  return null;
};

@Component({
  selector: 'app-profile-dialog',
  templateUrl: './profile.dialog.html',
  styleUrls: ['./profile.dialog.scss'],
  imports: [
    CommonModule,
    AppCommonModule,
    PickMediaComponent,
    ModalShellComponent,
  ],
})
export class ProfileDialogComponent {
  private contractorsService = inject(ContractorsService);
  private messagesService = inject(MessagesService);
  private workerMessagesService = inject(WorkerMessagesService);
  private navigationService = inject(NavigationService);
  private themeService = inject(ThemeService);
  private modalCtrl = inject(ModalController);
  private uploadService = inject(UploadService);
  private destroyRef = inject(DestroyRef);

  accentColors = ACCENT_COLORS;
  currentAccentColor = this.themeService.getAccentColor();
  editing = signal(false);
  showAdvanced = signal(false);
  showPasswordForm = signal(false);
  saveError = signal('');
  private editSnapshot = signal<Partial<User> | null>(null);
  user = computed(() => this.usersService.currentUserS());

  role = this.usersService.role;

  formGroup = computed(() => {
    const user = this.user();
    if (user) {
      return this.formBuilder.group({
        displayName: [user.displayName, Validators.required],
        email: [user.email, [Validators.email]],
        languages: [user.languages ?? []],
        iban: [user.iban],
        phone: [user.phone],
        workSafetyCard: [user.workSafetyCard],
        workingPermit: [user.workingPermit],
        rateko: [user.rateko],
        morningDigest: [user.morningDigest !== false],
        fullHours: [user.workType === 'fullTime'],
        inactive: [user.inactive],
        hourlyRate: [user.hourlyRate],
      });
    } else {
      return null;
    }
  });

  passwordFormGroup = computed(() => {
    const user = this.user();
    return this.formBuilder.group({
      email: [user?.email, [Validators.required, Validators.email]],
      password: [null, Validators.required],
      newPassword: [null, [Validators.required, Validators.minLength(6)]],
      newPasswordAgain: [null, [Validators.required, validateSame]],
    });
  });

  loading = signal(false);
  noChanges = signal(true);
  userDocuments = userDocuments;

  currentLang$ = new BehaviorSubject<string>(this.translate.getCurrentLang() ?? 'fi');
  readonly langs = this.translate.getLangs() as `${Languages}`[];

  hasLang = memo((lang: string) => this.user().languages?.some((it) => it === lang));


  organizationName = computed(() => this.contractorsService.contractorS()?.name ?? this.translate.instant('profileWorkspace.companyFallback'));

  visibleLanguages = computed(() => (this.user()?.languages ?? []).slice(0, 3));
  hiddenLanguageCount = computed(() => Math.max((this.user()?.languages?.length ?? 0) - this.visibleLanguages().length, 0));
  availableAccentColors = computed(() => this.accentColors.map((accent) => ({
    value: accent.value,
    label: accent.label,
  })));

  currentAccentLabel = computed(() =>
    this.availableAccentColors().find((option) => option.value === this.currentAccentColor())?.label
    ?? this.translate.instant('profileWorkspace.settings.defaultAccentLabel'));

  messages$ = this.usersService.getCurrentUser().pipe(
    filter((user) => user != null),
    switchMap((user) => this.messagesService.getList({ user: user.guid })),
    map((items) => items.sort((a, b) => sortAnyByDate(a, b, 'created')).reverse()),
    switchMap((items) => combineLatest([of(items), this.currentLang$])),
    map(([items, _lang]) => items.map((it) => {
      let name = '';
      let ticketName = '';
      let action: () => void;
      if (it.data?.startsWith(notificationKeys.ticket)) {
        const [ticketGuid, roomTicketGuid] = it.data.substring(it.data.indexOf('-') + 1).split('$');
        ticketName = this.translate.instant(it.title as string) as string;
        name = `${this.translate.instant(it.body as string) as string}`;
        action = () => this.workerMessagesService.onTicket(ticketGuid, roomTicketGuid, it);
      } else if (it.data?.startsWith(notificationKeys.reservation)) {
        const [, reservationGuid, siteGuid] = it.data.split('-');
        name = `${this.translate.instant(it.title as string) as string}<br/>${it.body as string}`;
        action = () => this.workerMessagesService.onReservation(siteGuid, reservationGuid, it);
      }
      return Object.assign({}, it, { name, action, ticketName });
    })),
    map((items) => items.filter((it) => !it.deleted && !it.read && it.body !== ticketCreated && it.sender.guid == null)),
    map((items) => items.slice(0, 3)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private loadingService: LoadingService,
    private notificationsService: PushNotificationsService,
    private translate: TranslateService,
    private usersService: UsersService,
  ) {
    this.translate.onLangChange.pipe(
      map((change) => change.lang),
      takeUntilDestroyed(),
    ).subscribe((lang) => {
      this.currentLang$.next(lang);
    });

    effect(() => {
      const formGroup = this.formGroup();
      if (formGroup) {
        formGroup.valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => this.noChanges.set(false));
      }
    });
  }

  private getLocalizedText(value: ILanguage | string | null | undefined, lang: string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (!value) {
      return '';
    }
    const languageKey = lang as keyof ILanguage;
    return value[languageKey] ?? value.fi ?? value.en ?? '';
  }

  private cloneEditableState(user: User): Partial<User> {
    return {
      image: user.image ?? null,
      languages: [...user.languages ?? []],
      morningDigest: user.morningDigest,

      iban: user.iban ?? '',
      phone: user.phone ?? '',
      displayName: user.displayName ?? '',
      email: user.email ?? '',
      workSafetyCard: user.workSafetyCard ?? false,
      workingPermit: user.workingPermit ?? false,
      rateko: user.rateko ?? false,
      inactive: user.inactive ?? false,
      hourlyRate: user.hourlyRate ?? null,
      workType: user.workType,
    };
  }

  startEditing(): void {
    const user = this.user();
    const formGroup = this.formGroup();
    if (!user || !formGroup) {
      return;
    }
    this.editSnapshot.set(this.cloneEditableState(user));
    this.saveError.set('');
    formGroup.reset({
      displayName: user.displayName,
      email: user.email,
      languages: [...user.languages ?? []],
      iban: user.iban ?? '',
      phone: user.phone ?? '',
      workSafetyCard: user.workSafetyCard ?? false,
      workingPermit: user.workingPermit ?? false,
      rateko: user.rateko ?? false,
      morningDigest: user.morningDigest !== false,
      fullHours: user.workType === 'fullTime',
      inactive: user.inactive ?? false,
      hourlyRate: user.hourlyRate ?? null,
    }, { emitEvent: false });
    this.noChanges.set(true);
    this.showPasswordForm.set(false);
    this.editing.set(true);
  }

  cancelEditing(): void {
    const snapshot = this.editSnapshot();
    const user = this.user();
    const formGroup = this.formGroup();
    if (snapshot && user) {
      Object.assign(user, {
        image: snapshot.image ?? null,
        languages: [...snapshot.languages ?? []],
        morningDigest: snapshot.morningDigest,

      });
    }
    if (formGroup && user) {
      formGroup.reset({
        displayName: user.displayName,
        email: user.email,
        languages: [...user.languages ?? []],
        iban: user.iban ?? '',
        phone: user.phone ?? '',
        workSafetyCard: user.workSafetyCard ?? false,
        workingPermit: user.workingPermit ?? false,
        rateko: user.rateko ?? false,
        morningDigest: user.morningDigest !== false,
        fullHours: user.workType === 'fullTime',
        inactive: user.inactive ?? false,
        hourlyRate: user.hourlyRate ?? null,
      }, { emitEvent: false });
    }
    this.noChanges.set(true);
    this.showPasswordForm.set(false);
    this.editSnapshot.set(null);
    this.saveError.set('');
    this.editing.set(false);
  }

  onCancel() {
    this.cancelEditing();
  }

  async closeProfile(result?: Record<string, unknown>): Promise<void> {
    const topModal = await this.modalCtrl.getTop();
    if (topModal) {
      await this.modalCtrl.dismiss(result);
    }
  }

  control(name: string): FormControl {
    return this.formGroup()?.get(name) as FormControl;
  }

  async logout() {
    const translateValues = await this.translate
      .get(['logout.title', 'logout.text'])
      .toPromise();
    const confirmRet = await Dialog.confirm({
      title: translateValues['logout.title'],
      message: translateValues['logout.text'],
    });
    if (confirmRet.value) {
      await this.closeProfile();
      this.notificationsService.removeCurrentToken().then(() => {
        this.authService.signOut();
      });
    }
  }

  onMessages() {
    this.navigationService.navigate(['worker', 'messages']);
  }

  onMessageClick(item: Message & { name: string; action: () => void; ticketName: string }) {
    item.action();
  }

  onLang(event: CustomEvent | Event, lang: string) {
    const checked = 'detail' in event
      ? event.detail.checked
      : (event.target as HTMLInputElement | null)?.checked;
    const currentLanguages = [...this.control('languages')?.value ?? this.user()?.languages ?? []];
    if (checked && !currentLanguages.includes(lang)) {
      currentLanguages.push(lang);
    }
    if (!checked) {
      const index = currentLanguages.indexOf(lang);
      if (index >= 0) {
        currentLanguages.splice(index, 1);
      }
    }
    if (this.user()) {
      this.user().languages = currentLanguages;
    }
    if (this.formGroup()) {
      this.control('languages').setValue(currentLanguages);
    }
    this.noChanges.set(false);
  }

  private logInvalidControls(group: FormGroup | AbstractControl, path = 'profileForm'): void {
    if (group instanceof FormGroup) {
      Object.entries(group.controls).forEach(([key, control]) => {
        const nextPath = `${path}.${key}`;
        if (control instanceof FormGroup) {
          this.logInvalidControls(control, nextPath);
          return;
        }
        if (control.invalid) {
          console.warn('profile invalid control', nextPath, control.errors, control.value);
        }
      });
    }
  }

  onAccentColor(accentColor: string) {
    this.loading.set(true);
    if (this.user()) {
      this.user().theme = accentColor;
    }
    this.themeService.setAccentColor(accentColor).then(() => {
      this.noChanges.set(false);
      this.loading.set(false);
    });
  }

  onAccentColorSelect(event: Event) {
    const accentColor = (event.target as HTMLSelectElement | null)?.value ?? 'blue';
    this.onAccentColor(accentColor);
  }

  onImageUrl(imageUrl: string) {
    this.user().image = imageUrl;
    this.noChanges.set(false);
  }

  async onFilesSelected(files: File[]) {
    const imageFile = files.find((file) => file.type.startsWith('image/'));
    if (!imageFile) {
      return;
    }
    const fileUpload = new FileUpload(imageFile);
    fileUpload.guid = `profile-${this.user()?.guid ?? 'user'}-${imageFile.name}-${new Date().toISOString()}`;
    const imageUrl = await this.uploadService.uploadImage(fileUpload);
    this.onImageUrl(imageUrl);
  }

  removeImage(): void {
    this.user().image = null;
    this.noChanges.set(false);
  }

  async save() {
    this.loading.set(true);
    this.saveError.set('');
    try {
      const activeForm = this.formGroup();
      if (!activeForm?.valid) {
        console.warn('profile save blocked: form invalid');
        this.logInvalidControls(activeForm);
        this.saveError.set(this.translate.instant('profileWorkspace.errors.invalidForm'));
        this.loading.set(false);
        return;
      }
      const formUser = this.formGroup().value;
      const user = this.user();
      if (!user?.guid) {
        this.loading.set(false);
        return;
      }
      const patch: Partial<User> = {
        displayName: formUser.displayName ?? user.displayName ?? '',
        phone: formUser.phone ?? user.phone ?? '',
        languages: formUser.languages ?? user.languages ?? [],
        iban: formUser.iban ?? user.iban ?? '',
        workSafetyCard: formUser.workSafetyCard ?? user.workSafetyCard ?? false,
        workingPermit: formUser.workingPermit ?? user.workingPermit ?? false,
        rateko: formUser.rateko ?? user.rateko ?? false,
        morningDigest: formUser.morningDigest ?? user.morningDigest,
        inactive: formUser.inactive ?? user.inactive ?? false,
        hourlyRate: formUser.hourlyRate ?? user.hourlyRate ?? null,
        workType: formUser.fullHours ? 'fullTime' : user.workType ?? null,
        image: user.image ?? null,
      };
      await this.usersService.updateOnly(user.guid, patch);
      Object.assign(user, patch);

      this.noChanges.set(true);
      this.loadingService.hideLoading();
      this.editing.set(false);
      this.editSnapshot.set(null);
      this.loading.set(false);
      this.saveError.set('');
    } catch (error) {
      console.error('profile save failed', error);
      this.saveError.set(this.translate.instant('profileWorkspace.errors.saveFailed'));
      this.loading.set(false);
    }
  }

  async delete() {
    const translateValues = await this.translate
      .get(['profile.deleteTitle', 'profile.deteleInfo'])
      .toPromise();
    const confirmRet = await Dialog.confirm({
      title: translateValues['profile.deleteTitle'],
      message: translateValues['profile.deteleInfo'],
    });
    if (confirmRet.value) {
      this.authService.deleteUser().then(async () => this.navigationService.navigateToHome());
    }
  }

  addedAttachments(user: User, attachments: IAttachment[]) {
    if (!user.attachments) {
      user.attachments = [];
    }
    user.attachments.push(...attachments);
    this.usersService.updateOnly(user.guid, { attachments: user.attachments });
  }

  selectLang(lang: `${Languages}`) {
    this.loading.set(true);
    if (this.user()) {
      this.user().lang = lang;
    }
    this.translate.use(lang);
    this.usersService.updateOnly(this.user().guid, { lang }).then(() => {
      this.noChanges.set(false);
      this.loading.set(false);
      this.currentLang$.next(lang);
    });
  }

  onLanguageSelect(event: Event) {
    const lang = (event.target as HTMLSelectElement | null)?.value as `${Languages}` | '';
    if (!lang) {
      return;
    }
    this.selectLang(lang);
  }

  async onMorningDigestToggle(event: CustomEvent<{ checked: boolean }>) {
    const user = this.user();
    if (!user?.guid) {
      return;
    }
    const nextValue = !!event.detail?.checked;
    const previousValue = user.morningDigest !== false;
    if (nextValue === previousValue) {
      return;
    }

    user.morningDigest = nextValue;
    const formGroup = this.formGroup();
    formGroup?.get('morningDigest')?.setValue(nextValue, { emitEvent: false });
    this.loading.set(true);

    try {
      await this.usersService.updateOnly(user.guid, { morningDigest: nextValue });
      this.noChanges.set(false);
    } catch (error) {
      console.error('morning digest update failed', error);
      user.morningDigest = previousValue;
      formGroup?.get('morningDigest')?.setValue(previousValue, { emitEvent: false });
      await Dialog.alert({ message: this.translate.instant('profileWorkspace.errors.saveFailed') });
    } finally {
      this.loading.set(false);
    }
  }

  updatePassword(formGroup: FormGroup) {
    const { email, password, newPassword } = formGroup.value;
    this.authService.updatePassword(email, password, newPassword).then(() => {
      this.showPasswordForm.set(false);
      this.editing.set(false);
    });
  }

  resetPassword() {
    const email = this.user()?.email;
    if (!email) {
      void Dialog.alert({ message: this.translate.instant('users.checkEmail') });
      return;
    }
    this.authService.resetPassword(email).then(() => {
      void Dialog.alert({ message: this.translate.instant('item.passwordResetSent') });
    });
  }



  changeEmail() {
    Dialog.prompt({
      title: this.translate.instant('users.email'),
      message: this.translate.instant('users.giveNewEmail'),
    }).then((confirmValue) => {
      if (confirmValue) {
        if (validateEmail(confirmValue.value)) {
          // Change
          this.loading.set(true);
          const user = this.user();
          user.email = confirmValue.value;
          this.usersService.updateEmail(user).pipe(take(1)).subscribe((ret) => {
            if (!ret.result) {
              Dialog.alert({ message: this.translate.instant('tickets.ai.error') });
            }
            this.loading.set(false);
          });
        } else {
          Dialog.alert({ message: this.translate.instant('users.checkEmail') });
        }
      }
    });
  }
}
